import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  ContactSearchQuery,
  RevealContactMutation,
  RevealContactMutationVariables,
  RevealContactsMutation,
  RevealContactsMutationVariables,
  ViewContactMutation,
} from "lib/generated/app-service-gql/graphql";
import { RevealStatus } from "lib/types/graphQLEnums";
import { useGraphQlMutation } from "../useGraphQlClient";
import { ContactSearchResult } from "./useContactSearch";

const revealContact = graphql(`
  mutation revealContact($input: RevealContactInput!) {
    revealContact(input: $input) {
      status
      contact {
        id
        redeemId
        tier
        firstName
        lastName
        fullName
        jobTitle
        account {
          id
          name
        }
        email
        phoneNumbers
        mobilePhoneNumbers
        directPhoneNumbers
        linkedinUrl
      }
    }
  }
`);

const revealContacts = graphql(`
  mutation revealContacts($input: RevealContactsInput!) {
    revealContacts(input: $input) {
      perItem {
        source
        id
        cognismContactId
        status
        contact {
          id
          redeemId
          tier
          firstName
          lastName
          fullName
          jobTitle
          account {
            id
            name
          }
          email
          phoneNumbers
          mobilePhoneNumbers
          directPhoneNumbers
          linkedinUrl
        }
      }
    }
  }
`);

type RevealedContact = NonNullable<RevealContactMutation["revealContact"]["contact"]>;
type ViewedContact = NonNullable<ViewContactMutation["viewContact"]["contact"]>;

/**
 * Patch every cached `contactSearch` page in place, replacing each node with its
 * revealed contact. Joins on the node `id` (Cognism's stable `contact_id`, Oscar's
 * oscar_contacts.id) — the same key the backend reconciles on — because a Cognism
 * `redeemId` can rotate server-side, so the reveal response's `redeemId` need not equal
 * the indexed node's; `redeemId` is only a fallback, and only for rows that have one
 * (Oscar rows don't, and two nulls must not join). Prefix-matching the query key covers
 * all cursor pages, so revealed rows survive paging without a refetch.
 */
function useWriteRevealedContactsToCache() {
  const queryClient = useQueryClient();

  return (revealed: RevealedContact[]) => {
    if (revealed.length === 0) return;
    const byId = new Map(revealed.map((c) => [c.id, c]));
    const byRedeemId = new Map(
      revealed.flatMap((c) => (c.redeemId ? [[c.redeemId, c] as const] : [])),
    );

    queryClient.setQueriesData<ContactSearchQuery>({ queryKey: ["contactSearch"] }, (prev) => {
      if (!prev) return prev;
      let changed = false;
      const nodes = prev.contactSearch.nodes.map((node) => {
        const match =
          byId.get(node.id) ?? (node.redeemId ? byRedeemId.get(node.redeemId) : undefined);
        if (!match) return node;
        changed = true;
        // Overlay the revealed values onto the existing preview node (keeps the
        // `has*` flags) and flip the tier so the row renders as redeemed.
        return { ...node, ...match } as ContactSearchResult;
      });
      return changed ? { ...prev, contactSearch: { ...prev.contactSearch, nodes } } : prev;
    });
  };
}

/** Reveal a single contact, writing the redeemed row back into the search cache on success. */
/**
 * @param options - Hook-level callbacks. Unlike those passed to `mutate`, these still run when
 * the calling component unmounts mid-reveal, and for every overlapping reveal.
 */
export function useRevealContact(
  options?: UseMutationOptions<RevealContactMutation, ClientError, RevealContactMutationVariables>,
) {
  const writeToCache = useWriteRevealedContactsToCache();
  return useGraphQlMutation(revealContact, {
    ...options,
    onSuccess: (data, variables, context) => {
      writeToCache([data.revealContact.contact]);
      return options?.onSuccess?.(data, variables, context);
    },
  });
}

export type RevealContactsItem = RevealContactsMutation["revealContacts"]["perItem"][number];

/**
 * Reveal contacts in bulk, across both sources, writing each revealed row back into the
 * search cache on success. Also the single-row path for an Oscar contact, as a batch of
 * one: Oscar has no redeemId for `revealContact` to take.
 */
export function useRevealContacts(
  options?: UseMutationOptions<
    RevealContactsMutation,
    ClientError,
    RevealContactsMutationVariables
  >,
) {
  const writeToCache = useWriteRevealedContactsToCache();
  return useGraphQlMutation(revealContacts, {
    ...options,
    onSuccess: (data, variables, context) => {
      // Persist REVEALED *and* SUPPRESSED rows — both return a REDEEMED-tier contact
      // (suppressed carries no email or phone; a Cognism one often still has a LinkedIn
      // URL, an Oscar one never does), so both should flip out of the preview state.
      const redeemed = data.revealContacts.perItem
        .filter(
          (item) =>
            (item.status === RevealStatus.Revealed || item.status === RevealStatus.Suppressed) &&
            item.contact,
        )
        .map((item) => item.contact as RevealedContact);
      writeToCache(redeemed);
      return options?.onSuccess?.(data, variables, context);
    },
  });
}

const viewContact = graphql(`
  mutation viewContact($input: ViewContactInput!) {
    viewContact(input: $input) {
      contact {
        id
        redeemId
        tier
        firstName
        lastName
        fullName
        jobTitle
        account {
          id
          name
        }
        email
        phoneNumbers
        mobilePhoneNumbers
        directPhoneNumbers
        linkedinUrl
      }
    }
  }
`);

/** Re-fetch a previously redeemed contact (no credit spent), writing the row back into the search cache. */
export function useViewContact() {
  const writeToCache = useWriteRevealedContactsToCache();
  return useGraphQlMutation(viewContact, {
    onSuccess: (data) => writeToCache([data.viewContact.contact as ViewedContact]),
  });
}
